import { Link, useLocation } from "react-router";
import {
  LayoutDashboard,
  FlaskConical,
  Search,
  Sigma,
  ChevronLeft,
  ChevronRight,
  Settings,
  Stethoscope,
  Check,
  LifeBuoy,
  Inbox,
} from "lucide-react";
import { ContactSupportDialog } from "@app/components/ContactSupportDialog";
import type { SpecialityDescriptor } from "@app/utils/speciality";
import { specialityLabel } from "@app/utils/speciality";
import { cn } from "@app/lib/utils";
import { Trans, msg, useLingui } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@app/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@app/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@app/components/ui/dropdown-menu";
import useUser from "@app/zustand/user";
import { useEffectiveSpecialite } from "@app/utils/use-effective-specialite";
import { useReadonlyScope } from "@app/utils/use-readonly-scope";
import { usePreviewView } from "@app/zustand/preview-view";
import { useAppVersion } from "@app/utils/use-app-version";
import { useTeleexpertiseAvailability } from "@app/utils/use-teleexpertise";
import API from "@app/services/api";
import { isInspectorRole } from "@app/utils/inspector-role";
import { useIsReviewer } from "@app/utils/use-is-reviewer";
import { useWritableSpecialites } from "@app/utils/use-writable-specialites";
import { isDemoEmail } from "@app/utils/demo-account";

interface NavItem {
  name: MessageDescriptor;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  paths?: string[]; // Additional paths that should highlight this item
  disabled?: boolean;
  /** Compteur affiché à droite du libellé — omis quand il vaut 0. */
  badge?: number;
}

const allNavItems: NavItem[] = [
  {
    name: msg`Tableau de bord`,
    icon: LayoutDashboard,
    path: "/dashboard",
    paths: ["/"],
  },
  {
    name: msg`Laboratoire`,
    icon: FlaskConical,
    path: "/laboratoire",
    paths: ["/laboratoire", "/datasets"],
  },
  {
    name: msg`Catalogue`,
    icon: Search,
    path: "/catalogue",
  },
  {
    name: msg`Études`,
    icon: Sigma,
    path: "/etudes",
  },
  {
    name: msg`Télé-expertise`,
    icon: Inbox,
    path: "/tele-expertise",
  },
];

export function SidebarNavigation() {
  const { i18n, t } = useLingui();
  const location = useLocation();
  const user = useUser((state) => state.user);
  const userRole = (user as { role?: string } | null)?.role;
  const previewRole = usePreviewView((state) => state.previewRole);
  const setSpecialite = usePreviewView((state) => state.setSpecialite);
  const effectiveRole = previewRole ?? userRole ?? "medecin";
  const isAdminReal = userRole === "admin";
  const isReviewer = useIsReviewer();
  const writableSpecialites = useWritableSpecialites();
  /**
   * Who gets the picker, what it shows, and whether it may be changed.
   *
   * The admin, over every speciality; a MAGE-X reviewer an admin granted another speciality, over
   * the ones they correct in and those only (PT-137) — the API ignores any other. A plain médecin
   * and the global persona have exactly one speciality and no picker at all.
   *
   * While the eye is previewing one of those single-speciality roles, the picker is a READOUT of
   * the previewed profile's speciality: the eye dominates every other affordance, and choosing a
   * second speciality from inside someone else's skin is not a state any of those roles can be in.
   * It used to reset the preview to « Vue admin » on click, which silently cancelled the eye.
   */
  const canBrowseSpecialites = isAdminReal || writableSpecialites.length > 1;
  const effectiveSpecialite = useEffectiveSpecialite();
  const canChooseSpecialite = previewRole === null || previewRole === "admin";
  /** Highlighted only when the speciality on screen is NOT the reader's own — admin included. */
  const ownSpecialite =
    (
      user as {
        own_specialite?: string | null;
        specialite?: string | null;
      } | null
    )?.own_specialite ??
    (user as { specialite?: string | null } | null)?.specialite ??
    null;
  const specialiteIsBorrowed =
    !!effectiveSpecialite && effectiveSpecialite !== ownSpecialite;
  /** Visiting, and only allowed to read there (PT-74). An admin keeps their hands. */
  const readonlyScope = useReadonlyScope();
  const [specialites, setSpecialites] = useState<SpecialityDescriptor[]>([]);
  const offeredSpecialites = isAdminReal
    ? specialites
    : specialites.filter((sp) => writableSpecialites.includes(sp.id));

  // A grant an admin withdrew leaves its speciality in the persisted store, where the API no longer
  // honours it: the session would read the account's own speciality under the old one's name.
  const previewSpecialite = usePreviewView((state) => state.previewSpecialite);
  useEffect(() => {
    if (!user || isAdminReal || !previewSpecialite) return;
    if (!writableSpecialites.includes(previewSpecialite)) setSpecialite(null);
  }, [
    user,
    isAdminReal,
    previewSpecialite,
    writableSpecialites,
    setSpecialite,
  ]);

  // Load supported specialities once for the "Vue spécialité" dropdown.
  useEffect(() => {
    if (!canBrowseSpecialites) return;
    let cancelled = false;
    (async () => {
      const res = (await API.get({ path: "/user/early-user-specialites" })) as {
        ok?: boolean;
        data?: { specialites?: SpecialityDescriptor[] };
      };
      if (cancelled) return;
      if (res?.ok && res.data?.specialites) {
        setSpecialites(res.data.specialites);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canBrowseSpecialites]);

  // The API answers in French — it serves the signup page too. The shared registry is what
  // carries the translations, so the label shown here follows the app's locale and falls back
  // to the API's own wording for a speciality the registry does not know yet.
  const activeSpecialiteLabel = useMemo(() => {
    if (!effectiveSpecialite) return null;
    const match = specialites.find((s) => s.id === effectiveSpecialite);
    return specialityLabel(effectiveSpecialite, t, match);
  }, [effectiveSpecialite, specialites, t]);

  const { frontend: frontendVersion, backend: backendVersion } =
    useAppVersion();
  const formatVersion = (v: string) =>
    /^[0-9a-f]{40}$/i.test(v) ? v.slice(0, 7) : v;
  const IS_DEMO = isDemoEmail((user as { email?: string } | null)?.email);

  /**
   * Le module de télé-expertise n'est ni un rôle ni une spécialité : il existe pour les comptes
   * dont la clinique a effectivement branché le formulaire web sur l'API, et pour personne d'autre.
   * La réponse vient du backend — le périmètre est une appartenance à une clinique, que lui seul
   * détient — jamais d'un rôle ou d'une adresse e-mail devinés ici.
   */
  const teleexpertise = useTeleexpertiseAvailability();

  // Médecin et démo : masque aussi les jeux de données.
  // Global : Tableau de bord + Catalogue + Études uniquement (pas de jeux de données ni de drill-down patient).
  const navItems = useMemo(() => {
    const visible = allNavItems
      .filter(
        (item) =>
          // Une entrée de menu menant à un écran définitivement vide est pire que pas d'entrée :
          // elle se lit comme un canal en panne.
          item.path !== "/tele-expertise" || teleexpertise.disponible,
      )
      .map((item) =>
        item.path === "/tele-expertise"
          ? { ...item, badge: teleexpertise.aTraiter }
          : item,
      );
    if (isInspectorRole(effectiveRole)) {
      return visible.filter(
        (item) =>
          item.path === "/dashboard" ||
          item.path === "/catalogue" ||
          item.path === "/etudes",
      );
    }
    // A médecin has no laboratory, the demo excepted: it opens it on its worked example (PT-105).
    // MAGE-X's reviewers keep it — the fondateur's laboratory (PT-119).
    if (effectiveRole === "medecin" && !IS_DEMO && !isReviewer) {
      return visible.filter((item) => item.path !== "/laboratoire");
    }
    return visible;
  }, [
    effectiveRole,
    IS_DEMO,
    isReviewer,
    teleexpertise.disponible,
    teleexpertise.aTraiter,
  ]);
  const [isHovered, setIsHovered] = useState(false);
  /** Le menu « Vue spécialité » est rendu dans un portail : tant qu'il est ouvert, la barre doit rester déployée. */
  const [specialiteMenuOpen, setSpecialiteMenuOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Check localStorage for saved state
    const saved = localStorage.getItem("sidebar-collapsed");
    return saved === "true";
  });

  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", String(isCollapsed));
    // Update CSS variable for layout
    document.documentElement.style.setProperty(
      "--sidebar-width",
      isCollapsed ? "4rem" : "16rem",
    );
  }, [isCollapsed]);

  const isActive = (item: NavItem) => {
    const pathname = location.pathname;
    // Exact match
    if (pathname === item.path) return true;
    // Check additional paths
    if (item.paths) {
      return item.paths.some((path) => {
        if (path === "/") {
          // For root path, only match exact "/"
          return pathname === "/";
        }
        return pathname === path || pathname.startsWith(path + "/");
      });
    }
    return false;
  };

  /** Barre déployée temporairement : survol, ou menu déroulant ouvert (le pointeur est alors hors de la barre). */
  const isPeeking = isHovered || specialiteMenuOpen;
  const sidebarWidth = isCollapsed && !isPeeking ? "4rem" : "16rem";
  const shouldShowLabels = !isCollapsed || isPeeking;
  /** Bande 4rem uniquement : logo « complet ». Dès que la barre fait 16rem (étendue ou repliée + survol), logo pictogramme. */
  const useWordmarkLogo = isCollapsed && !isPeeking;

  /**
   * The « Vue spécialité » row. One element either way, because it is a dropdown trigger while an
   * admin reads as themselves and a plain readout under the eye — same place, same wording, no
   * click target that would cancel the preview.
   */
  const specialiteLabelText = activeSpecialiteLabel ?? t`Vue spécialité`;
  /** What qualifies the speciality on the row — a preview it cannot leave, or a visit it may read. */
  const specialiteHint = !canChooseSpecialite
    ? t`Spécialité du profil prévisualisé`
    : readonlyScope
      ? t`Lecture seule`
      : null;
  const specialiteTrigger =
    isCollapsed && !isPeeking ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-disabled={!canChooseSpecialite}
            className={cn(
              "flex items-center justify-center w-full px-3 py-2.5 rounded-lg transition-all duration-200",
              canChooseSpecialite
                ? "hover:bg-slate-800/60 hover:text-slate-100"
                : "cursor-default",
              specialiteIsBorrowed ? "text-primary" : "text-slate-400",
            )}
            aria-label={t`Vue spécialité`}
          >
            <Stethoscope className="h-5 w-5" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          className="bg-slate-800 text-slate-100 border-slate-700"
        >
          {specialiteHint
            ? `${specialiteLabelText} · ${specialiteHint}`
            : specialiteLabelText}
        </TooltipContent>
      </Tooltip>
    ) : (
      <button
        type="button"
        aria-disabled={!canChooseSpecialite}
        title={specialiteHint ?? undefined}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 w-full",
          canChooseSpecialite
            ? "hover:bg-slate-800/60 hover:text-slate-100"
            : "cursor-default",
          specialiteIsBorrowed ? "text-primary" : "text-slate-400",
        )}
      >
        <Stethoscope className="h-5 w-5 flex-shrink-0" />
        <span
          className={cn(
            "whitespace-nowrap text-left flex-1 truncate",
            shouldShowLabels
              ? "opacity-100 max-w-full"
              : "opacity-0 max-w-0 overflow-hidden",
          )}
        >
          <span className="font-medium block truncate">
            {specialiteLabelText}
          </span>
          {specialiteHint && (
            <span className="block text-xs text-slate-500 truncate">
              {specialiteHint}
            </span>
          )}
        </span>
      </button>
    );

  const handleSpecialiteMenuOpenChange = (open: boolean) => {
    setSpecialiteMenuOpen(open);
    if (!isCollapsed) return;
    if (open) {
      document.documentElement.style.setProperty("--sidebar-width", "16rem");
      return;
    }
    // Le portail masque les événements de souris de la barre : à la fermeture,
    // on relit l'état de survol réel plutôt que de se fier au dernier mouseleave.
    const stillHovered = sidebarRef.current?.matches(":hover") ?? false;
    setIsHovered(stillHovered);
    if (!stillHovered) {
      document.documentElement.style.setProperty("--sidebar-width", "4rem");
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div
        ref={sidebarRef}
        className="fixed left-0 top-0 h-screen bg-slate-900 text-slate-100 transition-all duration-300 ease-in-out z-50 border-r border-slate-800/50 shadow-xl"
        style={{ width: sidebarWidth }}
        onMouseEnter={() => {
          if (isCollapsed) {
            setIsHovered(true);
            document.documentElement.style.setProperty(
              "--sidebar-width",
              "16rem",
            );
          }
        }}
        onMouseLeave={() => {
          if (isCollapsed && !specialiteMenuOpen) {
            setIsHovered(false);
            document.documentElement.style.setProperty(
              "--sidebar-width",
              "4rem",
            );
          }
        }}
      >
        {/* Logo Section */}
        <div
          className={cn(
            "flex flex-col gap-2 justify-center min-h-20 py-3 border-b border-slate-800",
            isCollapsed && !isPeeking ? "px-2" : "px-4",
          )}
        >
          <div
            className={cn(
              "flex items-center gap-2 w-full min-w-0",
              useWordmarkLogo ? "justify-center" : "justify-between",
            )}
          >
            <Link
              to="/dashboard"
              className={cn(
                "flex min-w-0",
                useWordmarkLogo
                  ? "items-center justify-center w-full"
                  : "items-center shrink-0",
              )}
            >
              <img
                key={useWordmarkLogo ? "wordmark" : "mark"}
                src={
                  useWordmarkLogo
                    ? "/logos/logo-mage-x-wordmark.png"
                    : "/logos/logo-mage-x-mark.png"
                }
                alt={t`Logo MAGE-X`}
                className={cn(
                  "object-contain",
                  useWordmarkLogo
                    ? "max-h-[4.5rem] w-full max-w-full object-center"
                    : "h-16 w-auto max-w-[min(100%,14rem)] object-left",
                )}
              />
            </Link>
            {!isCollapsed && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-slate-400 hover:text-slate-100 hover:bg-slate-800"
                onClick={() => setIsCollapsed(!isCollapsed)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="flex flex-col gap-1 p-2 mt-2">
          {navItems.map((item) => {
            const active = isActive(item);
            const Icon = item.icon;

            const content = (
              <Link
                to={item.disabled ? "#" : item.path}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative",
                  "hover:bg-slate-800/60",
                  active && "bg-slate-800/80 text-white font-medium",
                  !active && "text-slate-400 hover:text-slate-100",
                  item.disabled && "opacity-50 cursor-not-allowed",
                  isCollapsed && !isPeeking && "justify-center",
                )}
                onClick={(e) => item.disabled && e.preventDefault()}
              >
                {active && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                )}
                <Icon
                  className={cn(
                    "h-5 w-5 flex-shrink-0 transition-colors",
                    active && "text-white",
                    !active && "text-slate-400 group-hover:text-slate-100",
                  )}
                />
                <span
                  className={cn(
                    "font-medium transition-all duration-200 whitespace-nowrap",
                    shouldShowLabels
                      ? "opacity-100 max-w-full"
                      : "opacity-0 max-w-0 overflow-hidden",
                  )}
                >
                  {i18n._(item.name)}
                </span>
                {/* Le compteur suit le libellé quand la barre est déployée, et se réduit à une
                    pastille sur l'icône quand elle est repliée — sinon il disparaîtrait avec le
                    libellé, et c'est précisément ce qu'il sert à ne pas laisser passer. */}
                {!!item.badge && item.badge > 0 && (
                  <span
                    className={cn(
                      "flex items-center justify-center rounded-full bg-primary font-semibold text-white",
                      shouldShowLabels
                        ? "ml-auto h-5 min-w-5 px-1.5 text-xs"
                        : "absolute right-1.5 top-1.5 h-2 w-2 p-0 text-[0px]",
                    )}
                    aria-label={t`${item.badge} dossier(s) à traiter`}
                  >
                    {shouldShowLabels ? item.badge : ""}
                  </span>
                )}
              </Link>
            );

            const getTourId = (path: string) => {
              if (path === "/catalogue") return "sidebar-catalogue";
              if (path === "/laboratoire") return "sidebar-laboratoire";
              return undefined;
            };

            if (isCollapsed && !isPeeking) {
              const tourIdCollapsed = getTourId(item.path);
              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <div
                      {...(tourIdCollapsed
                        ? { "data-tour": tourIdCollapsed }
                        : {})}
                    >
                      {content}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="bg-slate-800 text-slate-100 border-slate-700"
                  >
                    {i18n._(item.name)}
                  </TooltipContent>
                </Tooltip>
              );
            }

            const tourId = getTourId(item.path);
            return (
              <div key={item.path} {...(tourId ? { "data-tour": tourId } : {})}>
                {content}
              </div>
            );
          })}
        </nav>

        {/* Bottom Section - Settings and version */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-800">
          {/* Vue spécialité — shows which speciality the session READS AS. Admins, and reviewers
              granted a second speciality (PT-137); changeable only while reading as themselves:
              under the eye it reports the previewed profile's speciality. */}
          {canBrowseSpecialites && (
            <div className="p-2 border-b border-slate-800/60">
              {canChooseSpecialite ? (
                <DropdownMenu
                  open={specialiteMenuOpen}
                  onOpenChange={handleSpecialiteMenuOpenChange}
                >
                  <DropdownMenuTrigger asChild>
                    {specialiteTrigger}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="right"
                    align="end"
                    className="w-56"
                  >
                    <DropdownMenuLabel>
                      <Trans>Explorer une spécialité</Trans>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {specialites.length === 0 && (
                      <DropdownMenuItem disabled>
                        <Trans>Chargement...</Trans>
                      </DropdownMenuItem>
                    )}
                    {offeredSpecialites.map((sp) => {
                      const isActive = effectiveSpecialite === sp.id;
                      return (
                        <DropdownMenuItem
                          key={sp.id}
                          onClick={() => setSpecialite(sp.id)}
                          className="flex items-center justify-between gap-2"
                        >
                          <span className="truncate">
                            {specialityLabel(sp.id, t, sp)}
                          </span>
                          {isActive && (
                            <Check className="h-4 w-4 text-primary shrink-0" />
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                    {specialiteIsBorrowed && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setSpecialite(null)}>
                          <Trans>Revenir à mon périmètre</Trans>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                specialiteTrigger
              )}
            </div>
          )}

          {/* Help / Contact Button */}
          <div className="p-2 pb-0">
            {isCollapsed && !isPeeking ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setShowSupportDialog(true)}
                    className={cn(
                      "flex items-center justify-center w-full px-3 py-2.5 rounded-lg transition-all duration-200",
                      "hover:bg-slate-800/60 text-slate-400 hover:text-slate-100",
                    )}
                    aria-label={t`Aide & contact`}
                  >
                    <LifeBuoy className="h-5 w-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  className="bg-slate-800 text-slate-100 border-slate-700"
                >
                  <Trans>Aide & contact</Trans>
                </TooltipContent>
              </Tooltip>
            ) : (
              <button
                type="button"
                onClick={() => setShowSupportDialog(true)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 w-full",
                  "hover:bg-slate-800/60 text-slate-400 hover:text-slate-100",
                )}
              >
                <LifeBuoy className="h-5 w-5 flex-shrink-0" />
                <span
                  className={cn(
                    "font-medium transition-all duration-200 whitespace-nowrap text-left",
                    shouldShowLabels
                      ? "opacity-100 max-w-full"
                      : "opacity-0 max-w-0 overflow-hidden",
                  )}
                >
                  <Trans>Aide & contact</Trans>
                </span>
              </button>
            )}
          </div>

          {/* Settings Button */}
          <div data-tour="sidebar-config" className="p-2">
            {isCollapsed && !isPeeking ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/configuration"
                    className={cn(
                      "flex items-center justify-center w-full px-3 py-2.5 rounded-lg transition-all duration-200",
                      "hover:bg-slate-800/60 text-slate-400 hover:text-slate-100",
                      location.pathname === "/configuration" &&
                        "bg-slate-800/80 text-white",
                    )}
                  >
                    <Settings className="h-5 w-5" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  className="bg-slate-800 text-slate-100 border-slate-700"
                >
                  <Trans>Configuration</Trans>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Link
                to="/configuration"
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative",
                  "hover:bg-slate-800/60",
                  location.pathname === "/configuration" &&
                    "bg-slate-800/80 text-white font-medium",
                  !(location.pathname === "/configuration") &&
                    "text-slate-400 hover:text-slate-100",
                )}
              >
                {location.pathname === "/configuration" && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                )}
                <Settings
                  className={cn(
                    "h-5 w-5 flex-shrink-0 transition-colors",
                    location.pathname === "/configuration" && "text-white",
                    !(location.pathname === "/configuration") &&
                      "text-slate-400 group-hover:text-slate-100",
                  )}
                />
                <span
                  className={cn(
                    "font-medium transition-all duration-200 whitespace-nowrap",
                    shouldShowLabels
                      ? "opacity-100 max-w-full"
                      : "opacity-0 max-w-0 overflow-hidden",
                  )}
                >
                  <Trans>Configuration</Trans>
                </span>
              </Link>
            )}
          </div>

          {/* Version and Collapse Button */}
          <div className="p-4 pt-0">
            {isCollapsed && !isPeeking && (
              <div className="flex items-center justify-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-slate-100 hover:bg-slate-800"
                  onClick={() => setIsCollapsed(!isCollapsed)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
            {(!isCollapsed || isPeeking) && (
              <div className="flex items-center justify-between">
                <div
                  className={cn(
                    "text-xs text-slate-500 transition-opacity duration-200",
                    shouldShowLabels ? "opacity-100" : "opacity-0",
                  )}
                  title={`Frontend: ${frontendVersion}${backendVersion ? ` · Backend: ${backendVersion}` : ""}`}
                >
                  <span>v{formatVersion(frontendVersion)}</span>
                  {backendVersion != null && backendVersion !== "?" && (
                    <span className="block truncate text-slate-600">
                      back. {formatVersion(backendVersion)}
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-slate-100 hover:bg-slate-800"
                  onClick={() => setIsCollapsed(!isCollapsed)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <ContactSupportDialog
        open={showSupportDialog}
        onOpenChange={setShowSupportDialog}
      />
    </TooltipProvider>
  );
}
